/** @jsx jsx */
import React from 'react';
import { jsx } from '@compiled/react';
import type {
	TreeItem,
	RenderItemParams,
	TreeDestinationPosition,
	TreeSourcePosition,
	TreeObject,
} from '@atlassian/jira-cmdb-pragmatic-dnd-tree/src/common/types.tsx';
import { Tree } from '@atlassian/jira-cmdb-pragmatic-dnd-tree/src/ui/index.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { createObjectTypeExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import {
	useInsightObjectSchemaPageData,
	useLoadedSchemaPageData,
	useSchemaPageUiState,
} from '@atlassian/jira-servicedesk-insight-object-schema-page-store/src/services/index.tsx';
import { ExperienceSuccess } from '@atlassian/ufo';
import {
	useObjectTypeTree,
	MoveObjectTypeError,
} from '../../../services/object-type-tree/index.tsx';
import { messages } from './messages.tsx';
import { ObjectTypeLink } from './object-type-link/index.tsx';

const DEFAULT_INDENT_LEVEL = 31;

export const ObjectTypeTree = () => {
	const [{ lastCreatedObjectTypeId }] = useInsightObjectSchemaPageData();
	const [{ objectTypesById, objectSchema, schemaGlobalConfig }] = useLoadedSchemaPageData();
	const [{ selectedObjectTypeId }] = useSchemaPageUiState();
	const { tree, setIsExpanded, moveObjectType } = useObjectTypeTree();
	const { showFlag } = useFlagsService();

	const renderItem = ({
		item,
		onExpand,
		onCollapse,
		indent = DEFAULT_INDENT_LEVEL,
	}: RenderItemParams<TreeItem>) => {
		const { isExpanded = true } = item;
		const objectType = objectTypesById[item.id];
		const { id } = objectType;

		return (
			<>
				<ObjectTypeLink
					objectType={objectType}
					isSelected={id === selectedObjectTypeId}
					isExpandable={item.children.length > 0}
					isExpanded={isExpanded}
					onExpand={() => onExpand?.(id)}
					onCollapse={() => onCollapse?.(id)}
					leftIndentation={indent}
					isPreview={false}
				/>
				{id === lastCreatedObjectTypeId && (
					<ExperienceSuccess experience={createObjectTypeExperience} />
				)}
			</>
		);
	};

	const renderDragPreview = (item: TreeItem) => {
		const objectType = objectTypesById[item.id];
		const { id } = objectType;

		return (
			<ObjectTypeLink
				objectType={objectType}
				isSelected={id === selectedObjectTypeId}
				isExpandable={item.children.length > 0}
				isExpanded={false}
				leftIndentation={0}
				isPreview
			/>
		);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getErrorDescription = (err: any) => {
		if (!(err && isValidationError(err))) {
			return messages.moveErrorDescription;
		}
		if (err.message === MoveObjectTypeError.NOT_MOVABLE_TO_INHERITED_TREE) {
			return messages.notMovableToInheritedTreeErrorMessage;
		}
		if (err.message === MoveObjectTypeError.NOT_MOVABLE_WHEN_INHERITED) {
			return messages.inheritedObjectTypeNotMovableErrorMessage;
		}
		return err.message;
	};

	const onDragEnd = (
		sourcePosition: TreeSourcePosition,
		destinationPosition: TreeDestinationPosition | null = null,
	) => {
		if (destinationPosition === null) {
			return;
		}

		moveObjectType({
			fromParentItemId: sourcePosition.parentId,
			fromIndex: sourcePosition.index,
			toParentItemId: destinationPosition.parentId,
			toIndex: destinationPosition.index ?? 0,
		}).catch((error) => {
			showFlag({
				type: 'error',
				title: messages.moveErrorTitle,
				description: getErrorDescription(error),
				isAutoDismiss: true,
			});
		});
	};

	const isDraggable = !objectSchema.isRestricted && schemaGlobalConfig.insightManager;

	return (
		<Tree
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			tree={tree as TreeObject<TreeItem>}
			renderItem={renderItem}
			dragPreview={renderDragPreview}
			onDragEnd={onDragEnd}
			onExpand={(objectTypeId: string) => setIsExpanded(objectTypeId, true)}
			onCollapse={(objectTypeId: string) => setIsExpanded(objectTypeId, false)}
			isDragEnabled={isDraggable}
			indentPerLevel={DEFAULT_INDENT_LEVEL}
		/>
	);
};
